import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/contexts";
import { useAccessToken, useTenantData } from "@/hooks";
import { WaiversPoliciesApi } from "@/openapi/generated";
import type { AcceptPolicyRequest } from "@/openapi/generated/models";
import type { PaginatedResultOfGetPolicyResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  readPendingWaiverSignatures,
  rememberPendingWaiverSignature,
  writePendingWaiverSignatures,
} from "@/lib/utils/waiver-signatures-session";

interface AcceptPolicyParams extends AcceptPolicyRequest {
  policyId: number;
}

function patchPolicyAcceptedAt(
  data: PaginatedResultOfGetPolicyResponse | undefined,
  policyId: number,
  acceptedAt: Date,
): PaginatedResultOfGetPolicyResponse | undefined {
  if (!data?.items) return data;

  return {
    ...data,
    items: data.items.map((item) =>
      Number(item.policyId) === policyId ? { ...item, acceptedAt } : item,
    ),
  };
}

export const usePolicyAccept = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();
  const user = useAuth();
  const userId = user?.id;

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async ({ policyId, ...acceptPolicyRequest }: AcceptPolicyParams) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(WaiversPoliciesApi, accessToken);

      return service.apiPoliciesIdAcceptPost({
        id: policyId,
        acceptPolicyRequest,
        xTenantID: tenantId,
      });
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.Waivers, "byUserId"] });

      const snapshots = queryClient.getQueriesData<PaginatedResultOfGetPolicyResponse>({
        queryKey: [QueryKeys.Waivers, "byUserId"],
      });

      const acceptedAt = new Date();
      if (userId) {
        rememberPendingWaiverSignature(tenantId, userId, variables.policyId, acceptedAt);
      }
      queryClient.setQueriesData<PaginatedResultOfGetPolicyResponse>(
        { queryKey: [QueryKeys.Waivers, "byUserId"] },
        (old) => patchPolicyAcceptedAt(old, variables.policyId, acceptedAt),
      );

      return { snapshots, acceptedAt };
    },
    onError: (error: unknown, variables, context) => {
      if (userId) {
        const pending = readPendingWaiverSignatures(tenantId, userId);
        delete pending[variables.policyId];
        writePendingWaiverSignatures(tenantId, userId, pending);
      }

      context?.snapshots.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      const errorMessage =
        error instanceof Error ? error.message : "Failed to sign waiver";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onSuccess: async (_data, variables, context) => {
      await queryClient.refetchQueries({ queryKey: [QueryKeys.Waivers, "byUserId"] });

      const acceptedAt = context?.acceptedAt ?? new Date();
      if (userId) {
        rememberPendingWaiverSignature(tenantId, userId, variables.policyId, acceptedAt);
      }
      queryClient.setQueriesData<PaginatedResultOfGetPolicyResponse>(
        { queryKey: [QueryKeys.Waivers, "byUserId"] },
        (old) => {
          const signedItem = old?.items?.find(
            (item) => Number(item.policyId) === variables.policyId,
          );
          if (signedItem?.acceptedAt) return old;
          return patchPolicyAcceptedAt(old, variables.policyId, acceptedAt);
        },
      );

      toast({
        title: "Waiver signed",
        description: "Your acceptance has been recorded.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
